import { useMutation, useQueryClient } from "@tanstack/react-query"
import type {
	PostChatAcknowledgeBody,
	PostChatAcknowledgeResponse,
	ProjectDTO,
	PropertyDTO,
} from "@zplanem/responses"
import { useCallback } from "react"
import { apiFetch } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"

export function useAcknowledgeChat(
	propertyId: string | undefined,
	chatId: string | undefined,
	projectId?: string,
) {
	const queryClient = useQueryClient()
	const apiBase = projectId
		? `/api/common/properties/${propertyId}/projects/${projectId}`
		: `/api/common/properties/${propertyId}`
	const mutation = useMutation({
		mutationFn: (body: PostChatAcknowledgeBody) => {
			if (!propertyId) throw new Error("propertyId required")
			if (!chatId) throw new Error("chatId required")
			return apiFetch<PostChatAcknowledgeResponse>(
				`${apiBase}/chats/${chatId}/acknowledge`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(body),
				},
			)
		},
		onSuccess: (res) => {
			if (!propertyId || !chatId) return
			if (projectId) {
				queryClient.setQueryData<ProjectDTO>(
					queryKeys.project(propertyId, projectId),
					(cache) => {
						if (!cache?.chats) return cache
						return {
							...cache,
							chats: cache.chats.map((c) =>
								c.id === chatId
									? { ...c, unreadMessageCount: res.unreadMessageCount }
									: c,
							),
						}
					},
				)
				return
			}
			// `useProperty` unwraps the API envelope, so this cache holds a bare
			// `PropertyDTO` — not a `PropertyResponse`. Patch `chats` directly.
			queryClient.setQueryData<PropertyDTO>(
				queryKeys.property(propertyId),
				(cache) => {
					if (!cache?.chats) return cache
					return {
						...cache,
						chats: cache.chats.map((c) =>
							c.id === chatId
								? { ...c, unreadMessageCount: res.unreadMessageCount }
								: c,
						),
					}
				},
			)
		},
	})
	const acknowledge = useCallback(
		(messageId: string) => mutation.mutate({ messageId }),
		[mutation],
	)
	return { acknowledge }
}
